import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminRefundStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatPaise, formatDateTime } from '../../lib/format.js';
import { useAdminRefunds } from '../hooks/use-payments.js';
import { REFUND_STATUSES, type AdminRefundListItem, type RefundListParams } from '../types/payments.js';

const PAGE_SIZE = 20;

export function RefundsListPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const orderNumberDebounced = useDebouncedValue(orderNumber, 300);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);

  const params: RefundListParams = useMemo(
    () => ({
      orderNumber: orderNumberDebounced || undefined,
      status: status ? [status as never] : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [orderNumberDebounced, status, page],
  );

  const { data, isLoading, isError, refetch, isFetching } = useAdminRefunds(params);
  const rows = data ?? [];

  const columns: AdminDataTableColumn<AdminRefundListItem>[] = [
    {
      key: 'orderNumber',
      header: 'Order',
      render: (row) => (
        <Link to={`${ROUTE_NAMES.adminOrders}/${row.orderNumber}`} className="font-medium text-gray-900 hover:underline">
          {row.orderNumber}
        </Link>
      ),
    },
    { key: 'amountPaise', header: 'Amount', render: (row) => formatPaise(row.amountPaise) },
    { key: 'status', header: 'Status', render: (row) => <AdminRefundStatusBadge status={row.status} /> },
    { key: 'reason', header: 'Reason', render: (row) => row.reason ?? '—' },
    { key: 'initiatedAt', header: 'Initiated', render: (row) => formatDateTime(row.initiatedAt) },
    { key: 'processedAt', header: 'Processed', render: (row) => (row.processedAt ? formatDateTime(row.processedAt) : '—') },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Refunds"
        description="Refund history across every payment."
        breadcrumbs={[{ label: 'Payments', to: ROUTE_NAMES.adminPayments }, { label: 'Refunds' }]}
        actions={<AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />}
      />

      <AdminSectionCard title="All refunds" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput
            value={orderNumber}
            onChange={(value) => {
              setOrderNumber(value);
              setPage(0);
            }}
            placeholder="Order number…"
            label="Search order number"
          />
          <AdminSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(0);
            }}
            allLabel="All statuses"
            options={REFUND_STATUSES.map((s) => ({ value: s, label: s }))}
          />
        </AdminFilterBar>

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No refunds match these filters"
        />

        <nav aria-label="Pagination" className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </AdminSectionCard>
    </AdminContent>
  );
}
